import { useQuery } from '@apollo/client';
import { orderBy } from 'lodash';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import ExportProjectsCsvButton from './ExportProjectsCsvButton';
import { MULTI_YEAR } from '../../constants/years';

export const sliceYearArray = (arr, startYearIndex, endYearIndex) => {
  // if the indices start after the end of the array
  // or end before the start of the array, return an empty array
  if (endYearIndex < 0 || startYearIndex >= arr.length) {
    return [];
  }

  // otherwise, clip the indices to the start and end of the array
  // for instance, if startYearIndex is -5 and yearIndex is 5,
  // we want to return the first five elements starting at 0
  return arr.slice(
    Math.max(0, startYearIndex),
    Math.min(arr.length, endYearIndex + 1),
  );
};

const getYearIndices = (year, years, startYear, farm) => {
  const farmStartYear = farm.startYear;
  if (year !== MULTI_YEAR) {
    return {
      startYearIndex: year - farmStartYear,
      endYearIndex: year - farmStartYear,
    };
  }

  const startYearIndex = startYear - farmStartYear;
  return {
    startYearIndex,
    endYearIndex: startYearIndex + years - 1,
  };
};

const ExportDashboardYearOrYearsButton = ({
  columns,
  query,
  // data transform should take in the data for a single farm
  // and return an array of objects, each of which will be a row in the CSV
  dataTransform,
  name,
  // year is either a single absolute year (e.g. 2020) or MULTI_YEAR
  year,
  // if year is MULTI_YEAR, years is a number of years to use in a range
  years,
  // the earliest start year of any farm in the dashboard
  // which is the year we'll start our year range from if year is MULTI_YEAR
  startYear,
}) => {
  const {
    dashboards: { dashboardFarms },
  } = useMultiProjectDashboardContext();

  const { refetch: refetchQuery } = useQuery(query, {
    variables: { ids: dashboardFarms.map((farm) => farm.id) },
    skip: true,
  });

  const fetchData = async () => {
    const { data } = await refetchQuery();

    const orderedFarms = orderBy(data.farms, (f) => f.name.toLowerCase());
    return orderedFarms.flatMap((farmData) => {
      const rows = dataTransform(farmData);

      // get the start and end year indices for this farm
      const { startYearIndex, endYearIndex } = getYearIndices(
        year,
        years,
        startYear,
        farmData,
      );

      // add startYearIndex, endYearIndex, and farmName to each row
      // to be used in creating our columns for the CSV
      const augmentedRows = rows.map((row) => ({
        ...row,
        startYearIndex,
        endYearIndex,
        farmName: farmData.name,
      }));
      return augmentedRows;
    });
  };

  const filename = `${name}_${
    year === MULTI_YEAR ? `${startYear}-${startYear + years - 1}` : year
  }.csv`;
  const exportType = `${name}${
    year === MULTI_YEAR ? `MultiYear` : 'SingleYear'
  }`;
  return (
    <ExportProjectsCsvButton
      columns={columns}
      fetchData={fetchData}
      // TODO: For persisted dashboard, add dashboard name to filename?
      filename={filename}
      exportType={exportType}
    />
  );
};

export default ExportDashboardYearOrYearsButton;
